<template>
  <article class="jcard" :class="{ 'jcard--legacy': joke.isLegacy }">
    <header class="jcard__head">
      <router-link :to="`/item/${joke.id}`" class="jcard__id">#{{ joke.id }}</router-link>
      <span v-if="joke.tags && joke.tags.length" class="jcard__tags">
        <router-link
          v-for="t in joke.tags"
          :key="t.slug"
          :to="{ path: '/rechercher', query: { tags: t.slug } }"
          class="jcard__tag"
          :class="{ 'jcard__tag--ton': t.axis === 'TON' }"
        >{{ t.label }}</router-link>
      </span>

      <span
        v-if="joke.rating"
        class="jcard__legacy"
        tabindex="0"
        aria-label="Score historique de la blague sur le site original (labanane.org)"
      >
        👑 {{ formattedRating }}
        <span class="jcard__tip" role="tooltip">
          Score historique de la blague sur le site original (labanane.org)
        </span>
      </span>

      <span class="jcard__spacer"></span>

      <div class="jcard__actions">
        <button
          class="jact jact--fav"
          :class="{ 'is-active': fav }"
          :aria-pressed="fav"
          :aria-label="fav ? 'Retirer des favoris' : 'Ajouter aux favoris'"
          title="Favori"
          @click="toggleFavorite"
        >
          <span class="jact__icon">{{ fav ? '★' : '☆' }}</span>
        </button>

        <ShareButton :joke-id="joke.id" variant="card" />
      </div>
    </header>

    <router-link :to="`/item/${joke.id}`" class="jcard__text">{{ joke.text }}</router-link>

    <footer class="jcard__foot">
      <span class="jcard__author">
        <template v-if="joke.isLegacy">🍌 <span class="jcard__legacy-name">labanane legacy</span></template>
        <template v-else>✍️ {{ joke.authorName }}</template>
      </span>

      <div class="jcard__votes">
        <button
          class="vote vote--up"
          :class="{ active: myVote === 1 }"
          :disabled="voting"
          :aria-pressed="myVote === 1"
          aria-label="J'aime"
          @click="castVote(1)"
        >
          <span class="vote__icon">▲</span>
        </button>
        <span class="vote__score" :class="scoreClass">{{ scoreLabel }}</span>
        <button
          class="vote vote--down"
          :class="{ active: myVote === -1 }"
          :disabled="voting"
          :aria-pressed="myVote === -1"
          aria-label="Je n'aime pas"
          @click="castVote(-1)"
        >
          <span class="vote__icon">▼</span>
        </button>
      </div>
    </footer>
  </article>
</template>

<script>
import { VOTE_MUTATION } from '@/graphql/jokes'
import { TOGGLE_FAVORITE_MUTATION } from '@/graphql/social'
import { isAuthenticated } from '@/auth'
import { getAnonId } from '@/anon'
import ShareButton from '@/components/ShareButton.vue'

export default {
  name: 'JokeCard',
  components: { ShareButton },
  props: {
    joke: { type: Object, required: true },
  },
  data() {
    return {
      score: this.joke.score ?? 0,
      myVote: this.joke.myVote ?? null,
      voting: false,
      fav: this.joke.isFavorite ?? false,
      favBusy: false,
    }
  },
  computed: {
    formattedRating() {
      return new Intl.NumberFormat('fr-FR').format(this.joke.rating)
    },
    scoreLabel() {
      return this.score > 0 ? `+${this.score}` : `${this.score}`
    },
    scoreClass() {
      if (this.score > 0) return 'is-pos'
      if (this.score < 0) return 'is-neg'
      return 'is-zero'
    },
  },
  watch: {
    'joke.score'(v) { this.score = v ?? 0 },
    'joke.myVote'(v) { this.myVote = v ?? null },
    'joke.isFavorite'(v) { this.fav = v ?? false },
  },
  methods: {
    async castVote(value) {
      // No login required: logged-out visitors vote anonymously (cookie + IP
      // dedup on the backend). We send the anon id only when not authenticated.
      const anonId = isAuthenticated() ? undefined : getAnonId()
      this.voting = true
      try {
        const { data } = await this.$apollo.mutate({
          mutation: VOTE_MUTATION,
          variables: { jokeId: parseInt(this.joke.id, 10), value, anonId },
        })
        this.myVote = data.vote.myVote
        this.score = data.vote.joke.score
      } catch (e) {
        console.error('Vote failed:', e)
      } finally {
        this.voting = false
      }
    },

    async toggleFavorite() {
      if (!isAuthenticated()) {
        this.$router.push({ path: '/login', query: { redirect: this.$route.fullPath } })
        return
      }
      if (this.favBusy || this.joke.id == null) return
      this.favBusy = true
      const previous = this.fav
      // Optimistic flip
      this.fav = !previous
      try {
        const { data } = await this.$apollo.mutate({
          mutation: TOGGLE_FAVORITE_MUTATION,
          variables: { jokeId: parseInt(this.joke.id, 10) },
        })
        this.fav = data.toggleFavorite.isFavorite
      } catch (e) {
        console.error('Toggle favorite failed:', e)
        this.fav = previous // revert
      } finally {
        this.favBusy = false
      }
    },
  },
}
</script>

<style scoped>
.jcard {
  position: relative;
  background: var(--surface);
  border: var(--border-w) solid var(--border-c);
  border-radius: var(--r-lg);
  padding: 18px 20px 16px;
  box-shadow: var(--shadow-md);
  transition: transform 0.2s var(--ease-out), box-shadow 0.2s var(--ease-out);
  display: flex;
  flex-direction: column;
  gap: 14px;
}
.jcard:hover { transform: translateY(-3px); box-shadow: var(--shadow-lg); }
.jcard--legacy::after {
  /* faint aged corner tint for legacy jokes */
  content: '';
  position: absolute;
  inset: 0;
  border-radius: var(--r-lg);
  pointer-events: none;
  background: radial-gradient(120px 80px at 100% 0%, rgba(203,132,0,0.08), transparent 70%);
}

.jcard__head { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
.jcard__id {
  font-family: var(--font-display);
  font-weight: 800;
  font-size: 1.05rem;
  color: var(--banana-700);
  transition: color 0.2s;
}
.jcard__id:hover { color: var(--bruise); }

.jcard__tags { display: inline-flex; flex-wrap: wrap; gap: 5px; align-items: center; }
.jcard__tag {
  font-family: var(--font-body);
  font-weight: 700;
  font-size: 0.68rem;
  text-transform: uppercase;
  letter-spacing: 0.02em;
  padding: 2px 9px;
  border-radius: var(--r-pill);
  border: 1.5px solid var(--border-c);
  background: var(--banana-100);
  color: var(--ink-800);
  transition: background 0.15s, transform 0.15s;
  white-space: nowrap;
}
.jcard__tag:hover { background: var(--banana-300); transform: translateY(-1px); }
/* Tone tags get the green accent to distinguish them from theme tags. */
.jcard__tag--ton { background: var(--peel-green); color: #fff; }
.jcard__tag--ton:hover { background: var(--peel-green-dark); }

.jcard__spacer { flex: 1; }
.jcard__legacy {
  position: relative;
  font-family: var(--font-display);
  font-weight: 700;
  font-size: 0.82rem;
  color: var(--ink-800);
  background: linear-gradient(180deg, var(--banana-200), var(--banana-300));
  border: 1.5px solid var(--banana-700);
  padding: 3px 10px;
  border-radius: var(--r-pill);
  cursor: help;
  outline: none;
}

/* CSS tooltip on the legacy crown */
.jcard__tip {
  position: absolute;
  top: calc(100% + 8px);
  left: 50%;
  transform: translateX(-50%) translateY(4px);
  z-index: 40;
  width: max-content;
  max-width: 220px;
  padding: 8px 11px;
  font-family: var(--font-body, inherit);
  font-weight: 600;
  font-size: 0.72rem;
  line-height: 1.35;
  text-align: center;
  color: var(--banana-400);
  background: var(--ink-900);
  border: var(--border-w) solid var(--border-c);
  border-radius: var(--r-md);
  box-shadow: var(--shadow-pop);
  opacity: 0;
  visibility: hidden;
  pointer-events: none;
  transition: opacity 0.16s var(--ease-out), transform 0.16s var(--ease-out);
}
.jcard__tip::before {
  content: '';
  position: absolute;
  bottom: 100%;
  left: 50%;
  transform: translateX(-50%);
  border: 6px solid transparent;
  border-bottom-color: var(--ink-900);
}
.jcard__legacy:hover .jcard__tip,
.jcard__legacy:focus-visible .jcard__tip {
  opacity: 1;
  visibility: visible;
  transform: translateX(-50%) translateY(0);
}

/* Header action buttons (favorite + share) */
.jcard__actions { display: inline-flex; align-items: center; gap: 4px; }
.jact {
  position: relative;
  width: 30px; height: 30px;
  display: grid; place-items: center;
  padding: 0;
  border: var(--border-w) solid var(--border-c);
  background: var(--surface);
  border-radius: 50%;
  cursor: pointer;
  color: var(--ink-500);
  transition: transform 0.15s var(--ease-bounce), background 0.15s var(--ease-out), color 0.15s var(--ease-out);
}
.jact:hover { transform: scale(1.12); }
.jact:active { transform: scale(0.96); }
.jact__icon { font-size: 0.9rem; line-height: 1; }
.jact--fav .jact__icon { color: var(--ink-400); }
.jact--fav.is-active { background: var(--banana-500); color: var(--ink-900); }
.jact--fav.is-active .jact__icon { color: var(--ink-900); }

.jcard__text {
  font-size: 1.12rem;
  line-height: 1.6;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
  word-break: break-word;
  color: var(--ink-900);
  display: block;
}
.jcard__text:hover { color: #000; }

.jcard__foot { display: flex; align-items: center; justify-content: space-between; gap: 12px; padding-top: 4px; border-top: 1.5px dashed var(--line); }
.jcard__author { font-size: 0.82rem; color: var(--text-soft); font-weight: 600; display: inline-flex; align-items: center; gap: 5px; }
.jcard__legacy-name { font-family: var(--font-accent); font-style: italic; }

/* Vote pill */
.jcard__votes {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  background: var(--banana-50);
  border: var(--border-w) solid var(--border-c);
  border-radius: var(--r-pill);
  padding: 3px;
}
.vote {
  width: 30px; height: 30px;
  display: grid; place-items: center;
  border: none;
  background: transparent;
  border-radius: 50%;
  cursor: pointer;
  color: var(--ink-500);
  transition: all 0.15s var(--ease-bounce);
}
.vote__icon { font-size: 0.75rem; line-height: 1; }
.vote:hover:not(:disabled) { transform: scale(1.2); }
.vote:disabled { opacity: 0.5; cursor: wait; }
.vote--up.active { background: var(--peel-green); color: #fff; }
.vote--down.active { background: var(--bruise); color: #fff; }
.vote__score {
  min-width: 34px;
  text-align: center;
  font-family: var(--font-display);
  font-weight: 800;
  font-size: 0.95rem;
}
.vote__score.is-pos { color: var(--peel-green-dark); }
.vote__score.is-neg { color: var(--bruise); }
.vote__score.is-zero { color: var(--ink-300); }

/* ---- Responsive ---- */
@media (max-width: 560px) {
  .jcard { padding: 16px 15px 14px; gap: 12px; border-radius: var(--r-md); }
  .jcard__text { font-size: 1.05rem; }
  .jcard__foot { flex-wrap: wrap; gap: 8px; }
  /* Bigger touch targets for voting on mobile */
  .vote { width: 36px; height: 36px; }
  .jact { width: 34px; height: 34px; }
}
</style>
